/**
 * The activity feed, drawn.
 *
 * The words are `src/lib/activity.ts`'s: `describeActivity` for the sentence
 * every row gets, `activityDetail` for the before/after table an edit or a
 * delete gets underneath it. This file is only the drawing of them — see that
 * module for why the logic lives there and not here.
 */

import type { ActivityEntry } from '../api'
import { activityDetail, describeActivity, describeSnapshotLine, type ActivityDetail } from '../lib/activity'
import { staggerStyle } from '../motion-react'

export { describeActivity } from '../lib/activity'
export type { ActivityDetail, ActivityDiffRow } from '../lib/activity'

/** The before/after table for one feed row — nothing beyond what `activityDetail` found. */
function ActivityDetailPanel({
  detail,
  nameOf,
}: {
  detail: ActivityDetail
  nameOf?: Map<string, string>
}) {
  return (
    <details className="feed__detail">
      <summary>What changed</summary>
      {detail.rows.length > 0 && (
        <table className="feed__table">
          <thead>
            <tr>
              <th scope="col">Field</th>
              <th scope="col">Before</th>
              <th scope="col">After</th>
            </tr>
          </thead>
          <tbody>
            {detail.rows.map((row) => (
              <tr key={row.field}>
                <th scope="row">{row.label}</th>
                <td>{row.before}</td>
                <td>{row.after}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {detail.linesBefore.length > 0 && (
        <div className="feed__lines">
          <p className="muted note">{detail.linesAfter.length > 0 ? 'Split before' : 'How it was split'}</p>
          <ul>
            {detail.linesBefore.map((line, index) => (
              <li key={index}>{describeSnapshotLine(line, detail.currency, nameOf)}</li>
            ))}
          </ul>
        </div>
      )}
      {detail.linesAfter.length > 0 && (
        <div className="feed__lines">
          <p className="muted note">Split after</p>
          <ul>
            {detail.linesAfter.map((line, index) => (
              <li key={index}>{describeSnapshotLine(line, detail.currency, nameOf)}</li>
            ))}
          </ul>
        </div>
      )}
    </details>
  )
}

export function ActivityFeed({
  entries,
  isPending,
  isError,
  error,
  onOpenGroup,
  showGroup = true,
  currency = 'USD',
  nameOf,
}: {
  entries: ActivityEntry[]
  isPending: boolean
  isError: boolean
  error?: Error | null
  onOpenGroup?: (groupId: string) => void
  showGroup?: boolean
  currency?: string
  /**
   * Display names for the group's own members, so a split's before/after
   * reads "Ada owed $30.00" rather than "usr_ada owed $30.00". Left out on
   * the home screen's cross-group feed, which has no single group's members
   * loaded — a line's `userId` is shown as itself there, same as it always
   * was for a kind this build had nothing better to say about.
   */
  nameOf?: Map<string, string>
}) {
  if (isPending) return <p className="muted">Catching up…</p>
  if (isError) return <p className="bad">{error?.message ?? 'Something went wrong.'}</p>
  if (entries.length === 0) {
    return <p className="muted">Nothing has happened yet.</p>
  }

  return (
    <ol className="feed">
      {entries.map((entry, index) => {
        const detail = activityDetail(entry)
        return (
          <li key={entry.id} className="feed__row" style={staggerStyle(index)}>
            <div className="feed__summary">
              <span className="feed__what">{describeActivity(entry, currency)}</span>
              <span className="feed__when">
                {showGroup && onOpenGroup && (
                  <>
                    <button
                      type="button"
                      className="tag"
                      onClick={() => onOpenGroup(entry.groupId)}
                    >
                      {entry.groupName}
                    </button>{' '}
                  </>
                )}
                <time dateTime={entry.createdAt.replace(' ', 'T')}>
                  {entry.createdAt.slice(0, 10)}
                </time>
              </span>
            </div>
            {detail && <ActivityDetailPanel detail={detail} nameOf={nameOf} />}
          </li>
        )
      })}
    </ol>
  )
}
